// src/GoldTransactionTable.js
import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    TablePagination,
    Typography,
    Box,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Card,
    CardContent,
    Alert,
} from '@mui/material';
import { Download as DownloadIcon } from '@mui/icons-material';
import { CSVLink } from 'react-csv';

const GoldTransactionTable = () => {
    const [transactions, setTransactions] = useState([]);
    const [allTransactions, setAllTransactions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(100);
    const [totalRecords, setTotalRecords] = useState(0);
    const [selectedTransaction, setSelectedTransaction] = useState(null);
    const [transactionDetails, setTransactionDetails] = useState(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [detailsLoading, setDetailsLoading] = useState(false);
    const [authenticated, setAuthenticated] = useState(false);
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [authError, setAuthError] = useState(false);
    const componentRef = useRef();

    const handleLogin = (e) => {
        e.preventDefault();
        if (username === 'maxwingold' && password === 'maxwinGoldPass#@') {
            setAuthenticated(true);
            setAuthError(false);
        } else {
            setAuthError(true);
        }
    };

    const fetchData = async (page, limit) => {
        try {
            const response = await axios.post(
                'https://secure.maxwinind.com/api/v1/open/gold-transaction',
                {
                    page: page + 1,
                    limit,
                },
                {
                    headers: {
                        'Content-Type': 'application/json',
                    },
                }
            );

            if (response.data.status && response.data.data.success) {
                setTransactions(response.data.data.data);
                setTotalRecords(response.data.data.pagination.totalRecords);
            } else {
                setError('Failed to fetch data');
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const fetchAllDataForCSV = async () => {
        try {
            const response = await axios.post(
                'https://secure.maxwinind.com/api/v1/open/gold-transaction',
                {
                    page: 1,
                    limit: totalRecords > 0 ? totalRecords : 1000,
                },
                {
                    headers: {
                        'Content-Type': 'application/json',
                    },
                }
            );

            if (response.data.status && response.data.data.success) {
                setAllTransactions(response.data.data.data);
            }
        } catch (err) {
            console.error('Error fetching all data for CSV:', err);
        }
    };

    const fetchTransactionDetails = async (tx_id) => {
        setDetailsLoading(true);
        try {
            const response = await axios.post(
                'https://secure.maxwinind.com/api/v1/open/gold-transaction/get',
                { tx_id },
                {
                    headers: {
                        'Content-Type': 'application/json',
                    },
                }
            );

            if (response.data.status) {
                setTransactionDetails(response.data.data);
            } else {
                setError('Failed to fetch transaction details');
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setDetailsLoading(false);
        }
    };

    const handleTransactionClick = (transaction) => {
        if (transaction.mode === 'SALE') {
            setSelectedTransaction(transaction);
            fetchTransactionDetails(transaction.tx_id);
            setDialogOpen(true);
        }
    };

    const handleCloseDialog = () => {
        setDialogOpen(false);
        setTransactionDetails(null);
        setSelectedTransaction(null);
    };

    useEffect(() => {
        if (authenticated) {
            fetchData(page, rowsPerPage);
        }
    }, [page, rowsPerPage, authenticated]);

    useEffect(() => {
        if (authenticated && totalRecords > 0) {
            fetchAllDataForCSV();
        }
    }, [totalRecords, authenticated]);

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        const value = event.target.value;
        if (value === 'UNLIMITED') {
            setRowsPerPage(totalRecords);
            setPage(0);
        } else {
            setRowsPerPage(parseInt(value, 10));
            setPage(0);
        }
    };

    const prepareCSVData = () => {
        return allTransactions.map(transaction => ({
            'Transaction ID': transaction.tx_id,
            'Date': transaction.rate_validity,
            'PG Transaction ID': transaction.pg_transaction_id,
            'PG Payment Status': transaction.pg_payment_status,
            'Current Price': transaction.current_price,
            'Gold Amount': transaction.gold_amount,
            'Buy Price': transaction.buy_price,
            'Mode': transaction.mode,
            'Investor Name': `${transaction.investor.first_name} ${transaction.investor.last_name}`,
            'Investor Mobile': transaction.investor.mobile_number,
        }));
    };

    const renderObjectAsTable = (obj, title) => {
        if (!obj) return null;
        
        return (
            <Box sx={{ mt: 2 }}>
                <Typography variant="h6" gutterBottom>{title}</Typography>
                <TableContainer component={Paper}>
                    <Table size="small">
                        <TableBody>
                            {Object.entries(obj).map(([key, value]) => (
                                <TableRow key={key}>
                                    <TableCell sx={{ fontWeight: 'bold' }}>{key}</TableCell>
                                    <TableCell>{typeof value === 'object' ? JSON.stringify(value) : String(value)}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>
        );
    };

    if (!authenticated) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <Card sx={{ width: 400 }}>
                    <CardContent>
                        <Typography variant="h5" component="div" gutterBottom textAlign="center">
                            Gold Transactions Login
                        </Typography>
                        {authError && (
                            <Alert severity="error" sx={{ mb: 2 }}>
                                Invalid username or password
                            </Alert>
                        )}
                        <form onSubmit={handleLogin}>
                            <TextField
                                fullWidth
                                label="Username"
                                variant="outlined"
                                margin="normal"
                                value={username}
                                onChange={(e) => setUsername(e.target.value)}
                            />
                            <TextField
                                fullWidth
                                label="Password"
                                type="password"
                                variant="outlined"
                                margin="normal"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                            />
                            <Button
                                fullWidth
                                type="submit"
                                variant="contained"
                                sx={{ mt: 3, mb: 2 }}
                            >
                                Login
                            </Button>
                        </form>
                    </CardContent>
                </Card>
            </Box>
        );
    }

    if (loading) return <Typography>Loading...</Typography>;
    if (error) return <Typography color="error">Error: {error}</Typography>;

    return (
        <Box sx={{ width: '100%', p: 0, m: 0 }}>
            <Paper elevation={3} sx={{ p: 0, m: 0, width: '100%' }}>
                <Box ref={componentRef} sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h4" gutterBottom>
                            Mpe Gold Transactions tracker
                        </Typography>
                        <Box>
                            <CSVLink
                                data={prepareCSVData()}
                                filename="gold-transactions.csv"
                                onClick={fetchAllDataForCSV}
                            >
                                <Button
                                    variant="contained"
                                    color="secondary"
                                    startIcon={<DownloadIcon />}
                                    sx={{ ml: 1 }}
                                >
                                    Export to CSV
                                </Button>
                            </CSVLink>
                        </Box>
                    </Box>

                    <TableContainer component={Paper} sx={{ width: '100%', overflowX: 'auto' }}>
                        <Table sx={{ minWidth: 'max-content' }} size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Transaction ID</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Date</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>PG Transaction ID</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>PG Payment Status</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Current Price</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Gold Amount</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Buy Price</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Mode</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Investor Name</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Investor Mobile</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {transactions.map((transaction) => (
                                    <TableRow 
                                        key={transaction.tx_id}
                                        sx={{ 
                                            backgroundColor: transaction.mode === 'BUY' 
                                                ? 'rgba(144, 238, 144, 0.3)' 
                                                : 'rgba(255, 182, 193, 0.3)',
                                            '&:hover': {
                                                cursor: transaction.mode === 'SALE' ? 'pointer' : 'default',
                                            }
                                        }}
                                    >
                                        <TableCell 
                                            onClick={() => handleTransactionClick(transaction)}
                                            sx={{ 
                                                textDecoration: transaction.mode === 'SALE' ? 'underline' : 'none',
                                                color: transaction.mode === 'SALE' ? 'primary.main' : 'inherit'
                                            }}
                                        >
                                            {transaction.tx_id}
                                        </TableCell>
                                        <TableCell>{transaction.rate_validity}</TableCell>
                                        <TableCell>{transaction.pg_transaction_id}</TableCell>
                                        <TableCell>{transaction.pg_payment_status}</TableCell>
                                        <TableCell>{transaction.current_price}</TableCell>
                                        <TableCell>{transaction.gold_amount}</TableCell>
                                        <TableCell>{transaction.buy_price}</TableCell>
                                        <TableCell>{transaction.mode}</TableCell>
                                        <TableCell>
                                            {transaction.investor.first_name} {transaction.investor.last_name}
                                        </TableCell>
                                        <TableCell>{transaction.investor.mobile_number}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>

                <TablePagination
                    rowsPerPageOptions={[100, 200, 300, { value: 'UNLIMITED', label: 'UNLIMITED' }]}
                    component="div"
                    count={totalRecords}
                    rowsPerPage={rowsPerPage === totalRecords ? 'UNLIMITED' : rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    sx={{ width: '100%' }}
                    labelRowsPerPage="Rows per page:"
                />
            </Paper>

            <Dialog 
                open={dialogOpen} 
                onClose={handleCloseDialog}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    Transaction Details: {selectedTransaction?.tx_id}
                </DialogTitle>
                <DialogContent>
                    {detailsLoading ? (
                        <Typography>Loading details...</Typography>
                    ) : (
                        <>
                            {transactionDetails && (
                                <>
                                    <Typography variant="subtitle1" gutterBottom>
                                        Basic Information
                                    </Typography>
                                    <TableContainer component={Paper}>
                                        <Table size="small">
                                            <TableBody>
                                                <TableRow>
                                                    <TableCell sx={{ fontWeight: 'bold' }}>Investor Name</TableCell>
                                                    <TableCell>
                                                        {transactionDetails.investor?.first_name} {transactionDetails.investor?.last_name}
                                                    </TableCell>
                                                </TableRow>
                                                <TableRow>
                                                    <TableCell sx={{ fontWeight: 'bold' }}>Mobile Number</TableCell>
                                                    <TableCell>{transactionDetails.investor?.mobile_number}</TableCell>
                                                </TableRow>
                                                <TableRow>
                                                    <TableCell sx={{ fontWeight: 'bold' }}>Gold Amount</TableCell>
                                                    <TableCell>{transactionDetails.gold_amount}</TableCell>
                                                </TableRow>
                                                <TableRow>
                                                    <TableCell sx={{ fontWeight: 'bold' }}>Sell Price</TableCell>
                                                    <TableCell>{transactionDetails.sell_price}</TableCell>
                                                </TableRow>
                                                <TableRow>
                                                    <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                                                    <TableCell>
                                                        {transactionDetails.payment_status === 1 ? 'Completed' : 'Pending'}
                                                    </TableCell>
                                                </TableRow>
                                            </TableBody>
                                        </Table>
                                    </TableContainer>

                                    {renderObjectAsTable(transactionDetails.trn_request, "Transaction Request")}
                                    {renderObjectAsTable(transactionDetails.trn_response, "Transaction Response")}
                                </>
                            )}
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog}>Close</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default GoldTransactionTable;